import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  CheckCircle,
  Download,
  Home,
  Loader2,
  AlertCircle,
  Mail,
  Calendar,
  Music,
} from 'lucide-react';
import { format } from 'date-fns';
import { getContract, updateContractStatus, CONTRACT_STATUS } from '../services/contractService';

// Format date consistently
const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))) {
      const [year, month, day] = String(dateStr).split('-').map(Number);
      return format(new Date(year, month - 1, day), 'MMMM d, yyyy');
    }
    return format(new Date(dateStr), 'MMMM d, yyyy');
  } catch {
    return dateStr;
  }
};

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const contractId = searchParams.get('contract');
  const sessionId = searchParams.get('session_id');

  const [contract, setContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAndUpdateContract = async () => {
      if (!contractId) {
        setError('No contract information found');
        setIsLoading(false);
        return;
      }

      try {
        // Fetch contract details
        const contractData = await getContract(contractId);
        setContract(contractData);

        // Determine if this is a deposit or balance payment
        const isBalancePayment = contractData.status === CONTRACT_STATUS.DEPOSIT_PAID;
        const isAlreadyPaid = contractData.status === CONTRACT_STATUS.PAID;

        if (!isAlreadyPaid) {
          if (isBalancePayment) {
            // Balance payment - mark as fully paid
            await updateContractStatus(contractId, CONTRACT_STATUS.PAID, {
              paidAt: new Date(),
              stripeBalanceSessionId: sessionId,
            });

            // Send balance payment invoice email
            try {
              await fetch('/api/send-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  to: contractData.clientEmail,
                  subject: `Final Payment Received - Invoice for ${contractData.artistName}`,
                  type: 'balance-payment-invoice',
                  customerName: contractData.clientName,
                  artistName: contractData.artistName,
                  venue: contractData.venue,
                  eventDate: contractData.eventDate,
                  eventTime: contractData.eventTime,
                  depositAmount: contractData.depositAmount,
                  totalAmount: contractData.totalAmount,
                  balanceAmount: (contractData.totalAmount || 0) - (contractData.depositAmount || 0),
                  paidAt: new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }),
                  contractId: contractId,
                }),
              });
            } catch (emailErr) {
              console.error('Failed to send balance invoice email:', emailErr);
            }
          } else {
            // Deposit payment - mark as deposit paid
            await updateContractStatus(contractId, CONTRACT_STATUS.DEPOSIT_PAID, {
              depositPaidAt: new Date(),
              stripeSessionId: sessionId,
            });

            // Send deposit payment invoice email
            try {
              await fetch('/api/send-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  to: contractData.clientEmail,
                  subject: `Payment Received - Invoice for ${contractData.artistName}`,
                  type: 'payment-invoice',
                  customerName: contractData.clientName,
                  artistName: contractData.artistName,
                  venue: contractData.venue,
                  eventDate: contractData.eventDate,
                  eventTime: contractData.eventTime,
                  depositAmount: contractData.depositAmount,
                  totalAmount: contractData.totalAmount,
                  paidAt: new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }),
                  contractId: contractId,
                }),
              });
            } catch (emailErr) {
              console.error('Failed to send deposit invoice email:', emailErr);
            }
          }
        }
      } catch (err) {
        console.error('Error processing payment success:', err);
        setError('Unable to load contract details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndUpdateContract();
  }, [contractId, sessionId]);

  const handleDownloadPDF = async () => {
    if (!contract) return;

    try {
      const [{ pdf }, { default: ContractTemplate }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../components/contracts/ContractTemplate'),
      ]);

      const blob = await pdf(
        <ContractTemplate contract={contract} signatureUrl={contract.signatureUrl} />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contract-${contract.id?.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Processing your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Payment Received</h1>
          <p className="text-gray-400 mb-6">
            Your payment was successful, but we couldn't load the contract details. Don't worry - you'll receive a confirmation email shortly.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-orange-500 to-red-600"
          >
            <Home className="w-4 h-4 mr-2" />
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4">
      <div className="container mx-auto max-w-2xl">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-8"
        >
          <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Payment Successful!</h1>
          <p className="text-gray-400 text-lg">
            Thank you for your deposit. Your booking is now confirmed!
          </p>
        </motion.div>

        {/* Booking Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Music className="w-5 h-5 text-orange-500" />
                Booking Confirmed
              </h2>

              {contract && (
                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b border-gray-800">
                    <span className="text-gray-400">Artist</span>
                    <span className="font-semibold">{contract.artistName}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-800">
                    <span className="text-gray-400">Package</span>
                    <span className="font-semibold">{contract.packageName}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-800">
                    <span className="text-gray-400">Event Date</span>
                    <span className="font-semibold">{formatDate(contract.eventDate)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-800">
                    <span className="text-gray-400">Venue</span>
                    <span className="font-semibold">{contract.venue}</span>
                  </div>
                  <div className="flex justify-between py-2 bg-green-500/10 rounded-lg px-3">
                    <span className="text-green-400">Deposit Paid</span>
                    <span className="font-bold text-green-400">
                      ${Number(contract.depositAmount).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-400">Remaining Balance</span>
                    <span className="font-semibold text-orange-500">
                      ${Number((contract.totalAmount || 0) - (contract.depositAmount || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-4">What's Next?</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-semibold">Confirmation Email</p>
                    <p className="text-sm text-gray-400">
                      You'll receive a confirmation email with all the details shortly.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-semibold">Event Day</p>
                    <p className="text-sm text-gray-400">
                      The remaining balance is due on or before the event date. Payment on the day must be in cash.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button
            onClick={handleDownloadPDF}
            className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Contract
          </Button>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-gray-700"
          >
            <Home className="w-4 h-4 mr-2" />
            Return Home
          </Button>
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-gray-500 text-sm mt-8"
        >
          Questions? Contact us at booking@scoobydagodson.com
        </motion.p>
      </div>
    </div>
  );
}
